import { Component, inject, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { TitleComponent } from '../../shared/components/title/title.component';
import { RestaurantService } from 'src/app/shared/services/restaurant/restaurant.service';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { MY_DATE_FORMATS } from '../news/incorporation-form/incorporation-form.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  HealthResponse,
  PunchJob,
} from 'src/app/shared/interfaces/utils.interface';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { QeqResponse } from 'src/app/shared/interfaces/qeq.interface';
import { UsersService } from 'src/app/shared/services/users/users.service';
import { SyncUser } from 'src/app/shared/interfaces/users.interface';
import { LoginService } from 'src/app/shared/services/login/login.service';
import { HolidaysService } from 'src/app/shared/services/holidays/holidays.service';
import { RemoteConfigResponse } from 'src/app/shared/interfaces/login.interface';
import { forkJoin } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';

// Register Spanish locale data so native date formatting uses day/month/year
registerLocaleData(localeEs);

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1; // Monday (0 = Sunday, 1 = Monday, etc.)
  }
}

@Component({
  selector: 'app-utils',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TitleComponent,
    MatTabGroup,
    MatTab,
    LoaderComponent,
    MatCheckboxModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './utils.component.html',
  styleUrls: ['./utils.component.css', '../../shared/styles/layout.css'],
})
export class UtilsComponent {
  title: string = 'Utilidades';
  subtitle: string = 'Herramientas y utilidades del sistema.';
  loading: boolean = false;
  jobUuid: string = '';
  syncForm!: FormGroup;
  punchForm!: FormGroup;
  versionForm!: FormGroup;
  resultPunchByDay: any = null;
  punchSyncro: PunchJob = {
    detail: '',
    status: '',
    job_id: '',
    started_at: '',
    date_range: {
      start_date: '',
      end_date: '',
    },
    status_url: '',
  };
  qeqResponse: QeqResponse | null = {
    data: [],
  };
  userManagementResponse: SyncUser[] | null = [
    {
      personnelnumber: '',
      name: '',
      jobid: '',
      titleid: '',
      srvrrhhisdriver: 0,
      image: '',
      imagefilename: '',
      show_image: false,
    },
  ];

  healthResponse: HealthResponse | null = {
    status: '',
    current_time: '',
    time: '',
    uptime: '',
    uptime_seconds: 0,
    debug: false,
  };

  threadsResponse: any = null;
  IndisponibilidadesResponse: any = null;
  xancletesResponse: any = null;
  xancletesStatusUrl: string = '';
  androidVersionResponse: RemoteConfigResponse | null = null;
  iosVersionResponse: RemoteConfigResponse | null = null;
  subscriptions: any;

  constructor(
    private restaurantService: RestaurantService,
    private loaderService: LoaderService,
    private qeqService: QeqService,
    private userService: UsersService,
    private fb: FormBuilder,
    private loginService: LoginService,
    private holidaysService: HolidaysService,
  ) {
    this.syncForm = this.fb.group({
      startDate: [new Date(), Validators.required],
      endDate: [new Date(), Validators.required],
    });
    this.punchForm = this.fb.group({
      startDate: [new Date(), Validators.required],
    });
    this.versionForm = this.fb.group({
      iosVersion: ['', [Validators.required, Validators.pattern(/^\d+\.\d+\.\d+$/)]],
      iosEnabled: [false],
      androidVersion: ['', [Validators.required, Validators.pattern(/^\d+\.\d+\.\d+$/)]],
      androidEnabled: [false],
    });
  }

  private _snackBar = inject(MatSnackBar);

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  ngOnInit() {
    this.loaderService.showLoader();

    forkJoin({
      health: this.userService.getHealth(),
      threads: this.userService.getThreads(),
      androidVersion: this.loginService.getAndroidVersion(),
      iosVersion: this.loginService.getIosVersion(),
    }).subscribe({
      next: (results) => {
        this.healthResponse = results.health;
        this.threadsResponse = results.threads;
        this.androidVersionResponse = results.androidVersion;
        this.iosVersionResponse = results.iosVersion;
        this.populateVersionForm();
        this.loaderService.hideLoader();
      },
      error: (err) => {
        console.error('Error loading initial data:', err);
        this.openSnackBar('Error al cargar datos iniciales');
        this.loaderService.hideLoader();
      },
    });
  }

  private populateVersionForm() {
    if (this.iosVersionResponse && this.androidVersionResponse) {
      this.versionForm.patchValue({
        iosVersion: this.iosVersionResponse.value,
        iosEnabled: this.iosVersionResponse.enabled,
        androidVersion: this.androidVersionResponse.value,
        androidEnabled: this.androidVersionResponse.enabled,
      });
    }
  }

  ngOnDestroy() {
    this.loaderService.hideLoader();
  }

  manageUsers() {
    this.loaderService.showLoader();
    this.userService.syncUsers().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.userManagementResponse = res;
        this.openSnackBar('Sincronización de usuarios completada.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al sincronizar usuarios');
      },
    });
  }

  pingQeQ() {
    this.loaderService.showLoader();
    this.qeqService.pingQeQ().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.qeqResponse = res;
        this.openSnackBar('Conexión exitosa a QeQ.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al conectar con QeQ');
      },
    });
  }

  pingHealth(init?: boolean) {
    this.loaderService.showLoader();
    this.userService.getHealth().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.healthResponse = res;
        // console.log('init', init);
        if (!init) {
          this.openSnackBar('Conexión exitosa al servicio de health.');
        }
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al conectar con el servicio de health');
      },
    });
  }

  pingThreads(init?: boolean) {
    this.loaderService.showLoader();
    this.userService.getThreads().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        // console.log(res);
        this.threadsResponse = res;
        if (!init) {
          this.openSnackBar('Conexión exitosa al servicio de threads.');
        }
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al conectar con el servicio de threads');
      },
    });
  }

  getAndroidVersion() {
    this.loaderService.showLoader();
    this.loginService.getAndroidVersion().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.androidVersionResponse = res;
        this.versionForm.patchValue({
          androidVersion: res.value,
          androidEnabled: res.enabled,
        });
        this.openSnackBar('Versión de Android obtenida correctamente.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al obtener versión de Android');
      },
    });
  }

  getIosVersion() {
    this.loaderService.showLoader();
    this.loginService.getIosVersion().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.iosVersionResponse = res;
        this.versionForm.patchValue({
          iosVersion: res.value,
          iosEnabled: res.enabled,
        });
        this.openSnackBar('Versión de iOS obtenida correctamente.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al obtener versión de iOS');
      },
    });
  }

  onSubmitVersions() {
    if (this.versionForm.invalid) return;

    const formValues = this.versionForm.getRawValue();
    this.loaderService.showLoader();

    forkJoin({
      ios: this.loginService.updateIosVersion({
        value: formValues.iosVersion,
        enabled: formValues.iosEnabled,
      }),
      android: this.loginService.updateAndroidVersion({
        value: formValues.androidVersion,
        enabled: formValues.androidEnabled,
      }),
    }).subscribe({
      next: (results) => {
        this.iosVersionResponse = results.ios;
        this.androidVersionResponse = results.android;
        this.populateVersionForm();
        this.openSnackBar('Versiones guardadas correctamente.');
        this.loaderService.hideLoader();
      },
      error: (err) => {
        console.error('Error al guardar versiones:', err);
        this.openSnackBar('Error al guardar las versiones');
        this.loaderService.hideLoader();
      },
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  private formatDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(d.getDate()).padStart(2, '0')}`;
  }

  onSubmit() {
    if (this.syncForm.invalid) return;
    const { startDate, endDate } = this.syncForm.getRawValue();
    if (!startDate || !endDate) return;
    if (endDate.getTime() < startDate.getTime()) {
      this.openSnackBar('La fecha fin no puede ser anterior a la fecha inicio');
      return;
    }
    this.loaderService.showLoader();
    this.loading = true;
    const startStr = this.formatDate(startDate);
    const endStr = this.formatDate(endDate);
    this.restaurantService.syncPunches(startStr, endStr).subscribe({
      next: (res) => {
        this.punchSyncro = res;
        this.openSnackBar('Sincronización completada.');
        this.jobUuid = res.job_id || '';
        this.loaderService.hideLoader();
      },
      error: (err) => {
        if (err.status === 409) {
          this.jobUuid = err.error?.job_id || '';
          this.openSnackBar(
            'Ya existe un trabajo en curso. Consulte su estado.',
          );
          this.loaderService.hideLoader();
          this.loading = false;
          return;
        } else {
          this.openSnackBar('Error al sincronizar fichajes por día');
          this.loaderService.hideLoader();
          this.loading = false;
        }
      },
      complete: () => (this.loading = false),
    });
  }

  // Sync punches for a single selected day. Called from the "PUNCHES POR DÍA" tab.
  onSubmitPunchDay() {
    if (this.punchForm.invalid) return;
    const { startDate } = this.punchForm.getRawValue();
    if (!startDate) return;
    this.loaderService.showLoader();
    this.loading = true;
    const dateStr = this.formatDate(startDate);
    // reuse syncPunches endpoint with the same start and end date
    this.restaurantService.getPunchesByDay(dateStr).subscribe({
      next: (res) => {
        this.resultPunchByDay = res;
        this.openSnackBar('Conexión exitosa al servicio de punches por día.');
        // this.punchSyncro = res;
        // this.jobUuid = res.job_id || '';
        // this.openSnackBar('Sincronización por día completada.');
        this.loaderService.hideLoader();
      },
      error: (err) => {
        if (err.status === 409) {
          this.jobUuid = err.error?.job_id || '';
          this.openSnackBar(
            'Ya existe un trabajo en curso. Consulte su estado.',
          );
          this.loaderService.hideLoader();
          this.loading = false;
          return;
        } else {
          this.openSnackBar('Error al sincronizar fichajes por día');
          this.loaderService.hideLoader();
          this.loading = false;
        }
      },
      complete: () => (this.loading = false),
    });
  }

  syncXancletes() {
    this.loaderService.showLoader();
    this.holidaysService.FixSeniorityEntitlements().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.xancletesResponse = res;
        this.xancletesStatusUrl = res.status_url || '';
        this.openSnackBar('Sincronización de Fechas iniciada.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al sincronizar Fechas');
      },
    });

    this.holidaysService.syncXancletesNightly().subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.xancletesResponse = res;
        this.xancletesStatusUrl = res.status_url || '';
        this.openSnackBar('Sincronización de Xancletes iniciada.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al sincronizar Xancletes');
      },
    });
  }

  checkXancletesStatus() {
    this.loaderService.showLoader();
    this.holidaysService.getXancletesJobStatus(this.xancletesStatusUrl).subscribe({
      next: (res) => {
        this.xancletesResponse = res;
        this.openSnackBar(`Estado del trabajo: ${res.status}`);
        this.loaderService.hideLoader();
      },
      error: (err) => {
        console.error(err);
        this.openSnackBar('Error al consultar el estado del trabajo');
        this.loaderService.hideLoader();
      },
    });
  }

  viewPunchResults() {
    if (this.jobUuid != '') {
      this.loaderService.showLoader();
      this.restaurantService.checkPunchJobStatus(this.jobUuid).subscribe({
        next: (res) => {
          this.openSnackBar(`Estado del trabajo: ${res.status}`);
          this.punchSyncro = res as any;
          this.loaderService.hideLoader();
        },
        error: (err) => {
          console.error(err);
          this.openSnackBar('Error al consultar el estado del trabajo');
          this.loaderService.hideLoader();
        },
      });
    } else {
      this.openSnackBar(
        'No hay ningún trabajo en curso. Inicie una sincronización primero.',
      );
    }
  }

  
  pingIndisponibilidades() {
    this.loaderService.showLoader();
    this.holidaysService.UnavailabilityEmployee(1541).subscribe({
      next: (res) => {
        this.loaderService.hideLoader();
        this.IndisponibilidadesResponse = res;
        this.xancletesStatusUrl = res.status_url || '';
        this.openSnackBar('Sincronización de Indisponibilidades iniciada.');
      },
      error: (err) => {
        this.loaderService.hideLoader();
        console.error(err);
        this.openSnackBar('Error al sincronizar Indisponibilidades');
      },
    });
  }
}
